import React, { useEffect, useState, useCallback } from 'react';
import { apiClient } from '../../services/api';
import { Copy, Check, Share2, UserPlus, DollarSign, Link2 } from 'lucide-react';
import { GlassCard } from '../ui/GlassCard';
import ModuleHeader from '../ui/ModuleHeader';
import './AffiliateDashboard.css';

/** Normalize FastAPI error detail — may be a string or a Pydantic validation error array */
const getApiError = (e: any, fallback: string): string => {
  const detail = e?.response?.data?.detail;
  if (!detail) return fallback;
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) {
    return detail.map((d: any) => d?.msg ?? JSON.stringify(d)).join('; ');
  }
  return fallback;
};

// ─── Types ────────────────────────────────────────────────────────────────────

interface EarningEntry {
  id: string;
  created_at: string;
  referred_user_email: string;
  amount_cents: number;
  status: 'pending' | 'approved' | 'paid' | 'voided';
  eligible_from: string | null;
}

interface DashboardData {
  referral_link: string | null;
  referral_code: string | null;
  pending_earnings_cents: number;
  approved_earnings_cents: number;
  paid_earnings_cents: number;
  earnings: EarningEntry[];
}

interface ReferredUser {
  id: string;
  email_masked: string;
  joined_at: string;
  plan: string | null;
  is_active_subscriber: boolean;
}

interface ReferredUsersResponse {
  total: number;
  referred_users: ReferredUser[];
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function formatCurrency(cents: number): string {
  return new Intl.NumberFormat('en-AU', {
    style: 'currency',
    currency: 'AUD',
    minimumFractionDigits: 2,
  }).format(cents / 100);
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString('en-AU', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

const STATUS_STYLES: Record<string, React.CSSProperties> = {
  pending:  { background: 'rgba(245,200,76,0.18)',  color: 'var(--warning)',   border: '1px solid rgba(245,200,76,0.35)' },
  approved: { background: 'rgba(33,119,255,0.12)',  color: 'var(--primary-500)', border: '1px solid rgba(33,119,255,0.25)' },
  paid:     { background: 'rgba(54,211,153,0.14)',  color: 'var(--success)',   border: '1px solid rgba(54,211,153,0.30)' },
  voided:   { background: 'rgba(100,116,139,0.12)', color: 'var(--text-secondary)', border: '1px solid var(--stroke)' },
};

// ─── Component ────────────────────────────────────────────────────────────────

const AffiliateDashboard: React.FC = () => {
  const [dashboardData, setDashboardData] = useState<DashboardData | null>(null);
  const [referredUsers, setReferredUsers] = useState<ReferredUsersResponse | null>(null);
  const [commissionRate, setCommissionRate] = useState<{
    current_rate_pct: number;
    is_promo_active: boolean;
    promo_rate_pct: number | null;
    promo_end: string | null;
    base_rate_pct: number;
  } | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [dashRes, usersRes] = await Promise.all([
        apiClient.get('/affiliate/dashboard'),
        apiClient.get('/affiliate/referred-users'),
      ]);
      setDashboardData(dashRes.data);
      setReferredUsers(usersRes.data);
      apiClient.get('/affiliate/commission-rate').then(res => setCommissionRate(res.data)).catch(() => {});
    } catch (err: any) {
      setError(getApiError(err, err?.message || 'Failed to load affiliate data.'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleCopy = useCallback((text: string) => {
    navigator.clipboard.writeText(text).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  }, []);

  const referralLink = dashboardData?.referral_link ?? null;
  const activeReferrals = referredUsers?.referred_users?.filter(u => u.is_active_subscriber).length ?? 0;

  // ── Loading / Error ──────────────────────────────────────────────────────

  if (loading) {
    return (
      <div className="affiliate-page">
        <ModuleHeader title="Affiliate Programme" breadcrumb={['Home', 'Affiliate']} />
        <div style={{ textAlign: 'center', padding: '3rem', color: 'var(--text-secondary)' }}>
          Loading affiliate data…
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="affiliate-page">
        <ModuleHeader title="Affiliate Programme" breadcrumb={['Home', 'Affiliate']} />
        <GlassCard style={{ marginTop: '1.5rem' }}>
          <p style={{ color: 'var(--danger)', margin: 0 }}>{error}</p>
        </GlassCard>
      </div>
    );
  }

  // ── Empty state (no referrals yet) ───────────────────────────────────────

  const hasAnyReferrals = (referredUsers?.total ?? 0) > 0 || (dashboardData?.earnings?.length ?? 0) > 0;

  return (
    <div className="affiliate-page">
      <ModuleHeader title="Affiliate Programme" breadcrumb={['Home', 'Affiliate']} />

      {/* ── Referral Link card ──────────────────────────────────────────── */}
      <GlassCard style={{ marginTop: '1.5rem' }}>
        <h2 style={{ margin: '0 0 0.4rem', fontSize: '1.1rem', fontWeight: 600, color: 'var(--text)' }}>
          Your Referral Link
        </h2>
        <p style={{ margin: '0 0 1rem', fontSize: '0.875rem', color: 'var(--text-secondary)' }}>
          Share this link — earn {commissionRate ? `${commissionRate.current_rate_pct}%` : '25%'} of net subscription revenue for every new paying subscriber.
        </p>
        {referralLink ? (
          <div className="affiliate-link-box">
            <span className="affiliate-link-text">{referralLink}</span>
            <button
              className="affiliate-copy-btn"
              onClick={() => handleCopy(referralLink)}
              title={copied ? 'Copied!' : 'Copy link'}
            >
              {copied ? <Check size={16} color="var(--success)" /> : <Copy size={16} />}
            </button>
          </div>
        ) : (
          <div className="affiliate-link-box affiliate-link-placeholder">
            <span style={{ color: 'var(--text-secondary)', fontStyle: 'italic' }}>
              Generating your link…
            </span>
          </div>
        )}
        {commissionRate?.is_promo_active && (
          <div style={{ background: 'var(--primary, #2177FF)18', border: '1px solid var(--primary, #2177FF)44', borderRadius: 8, padding: '0.6rem 1rem', fontSize: '0.875rem', color: 'var(--primary, #2177FF)', marginTop: '0.75rem' }}>
            🎉 Promotional rate active: <strong>{commissionRate.promo_rate_pct}%</strong> commission until {commissionRate.promo_end}
          </div>
        )}
      </GlassCard>

      {/* ── Stats row ───────────────────────────────────────────────────── */}
      <div className="affiliate-stats-grid" style={{ marginTop: '1.25rem' }}>
        <div className="affiliate-stat-card">
          <div className="affiliate-stat-value" style={{ color: 'var(--warning)' }}>
            {formatCurrency(dashboardData?.pending_earnings_cents ?? 0)}
          </div>
          <div className="affiliate-stat-label">Pending Earnings</div>
        </div>
        <div className="affiliate-stat-card">
          <div className="affiliate-stat-value" style={{ color: 'var(--primary-500)' }}>
            {formatCurrency(dashboardData?.approved_earnings_cents ?? 0)}
          </div>
          <div className="affiliate-stat-label">Approved Earnings</div>
        </div>
        <div className="affiliate-stat-card">
          <div className="affiliate-stat-value" style={{ color: 'var(--success)' }}>
            {formatCurrency(dashboardData?.paid_earnings_cents ?? 0)}
          </div>
          <div className="affiliate-stat-label">Total Paid Out</div>
        </div>
        <div className="affiliate-stat-card">
          <div className="affiliate-stat-value" style={{ color: 'var(--secondary-500)' }}>
            {activeReferrals}
          </div>
          <div className="affiliate-stat-label">Active Referrals</div>
        </div>
      </div>

      {/* ── How It Works ────────────────────────────────────────────────── */}
      <GlassCard style={{ marginTop: '1.25rem' }}>
        <h2 style={{ margin: '0 0 1rem', fontSize: '1.1rem', fontWeight: 600, color: 'var(--text)' }}>
          How It Works
        </h2>
        <div className="affiliate-how-it-works">
          <div className="affiliate-step">
            <div className="affiliate-step-icon">
              <Share2 size={20} color="var(--primary-500)" />
            </div>
            <div className="affiliate-step-num">Step 1</div>
            <div className="affiliate-step-desc">Share your unique link with your network</div>
          </div>
          <div className="affiliate-step">
            <div className="affiliate-step-icon">
              <UserPlus size={20} color="var(--primary-500)" />
            </div>
            <div className="affiliate-step-num">Step 2</div>
            <div className="affiliate-step-desc">They sign up for AiMe using your link</div>
          </div>
          <div className="affiliate-step">
            <div className="affiliate-step-icon">
              <DollarSign size={20} color="var(--primary-500)" />
            </div>
            <div className="affiliate-step-num">Step 3</div>
            <div className="affiliate-step-desc">You earn {commissionRate ? `${commissionRate.current_rate_pct}%` : '25%'} of their net subscription — every month</div>
          </div>
        </div>
      </GlassCard>

      {/* ── Payout Info ─────────────────────────────────────────────────── */}
      <GlassCard style={{ marginTop: '1.25rem' }}>
        <h2 style={{ margin: '0 0 0.75rem', fontSize: '1.1rem', fontWeight: 600, color: 'var(--text)' }}>
          Payout Information
        </h2>
        <p style={{ margin: '0 0 0.75rem', fontSize: '0.875rem', color: 'var(--text-secondary)', lineHeight: 1.6 }}>
          Commissions are held for one calendar month to allow for the refund window. Each commission
          becomes eligible for payout on the <strong style={{ color: 'var(--text)' }}>15th of the following month</strong>.
          Payouts are processed on the <strong style={{ color: 'var(--text)' }}>15th of each month</strong>. To register your
          payment details, contact{' '}
          <a href="mailto:info@clearbyte.au" style={{ color: 'var(--primary-500)' }}>
            info@clearbyte.au
          </a>
          .
        </p>
        <p style={{ margin: 0, fontSize: '0.825rem', color: 'var(--text-muted)', lineHeight: 1.5 }}>
          Commission rate: {commissionRate ? `${commissionRate.current_rate_pct}%` : '25%'} of net subscription revenue (after Stripe processing fees).
        </p>
      </GlassCard>

      {/* ── Empty state (no referrals) ───────────────────────────────────── */}
      {!hasAnyReferrals && referralLink && (
        <GlassCard style={{ marginTop: '1.25rem', textAlign: 'center', padding: '2.5rem 1.5rem' }}>
          <div style={{ marginBottom: '1rem' }}>
            <Link2 size={40} color="var(--primary-500)" style={{ opacity: 0.7 }} />
          </div>
          <h3 style={{ margin: '0 0 0.5rem', color: 'var(--text)', fontWeight: 600 }}>
            No referrals yet — start sharing!
          </h3>
          <p style={{ margin: '0 0 1.25rem', color: 'var(--text-secondary)', fontSize: '0.875rem' }}>
            Share your link below and start earning {commissionRate ? `${commissionRate.current_rate_pct}%` : '25%'} recurring commission on every subscriber you refer.
          </p>
          <div className="affiliate-link-box" style={{ maxWidth: 520, margin: '0 auto 1rem' }}>
            <span className="affiliate-link-text">{referralLink}</span>
            <button
              className="affiliate-copy-btn"
              onClick={() => handleCopy(referralLink)}
              title={copied ? 'Copied!' : 'Copy link'}
            >
              {copied ? <Check size={16} color="var(--success)" /> : <Copy size={16} />}
            </button>
          </div>
          <a
            href={`mailto:?subject=Try%20AIME&body=I%27ve%20been%20using%20AIME%20for%20my%20marketing%20—%20check%20it%20out%3A%20${encodeURIComponent(referralLink)}`}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '0.4rem',
              fontSize: '0.875rem',
              color: 'var(--primary-500)',
              textDecoration: 'none',
            }}
          >
            <Share2 size={15} />
            Share via Email
          </a>
        </GlassCard>
      )}

      {/* ── Earnings History table ───────────────────────────────────────── */}
      {(dashboardData?.earnings?.length ?? 0) > 0 ? (
        <GlassCard style={{ marginTop: '1.25rem' }} padding="none">
          <div style={{ padding: '1.25rem 1.25rem 0.75rem', borderBottom: '1px solid var(--stroke)' }}>
            <h2 style={{ margin: 0, fontSize: '1.1rem', fontWeight: 600, color: 'var(--text)' }}>
              Earnings History
            </h2>
          </div>
          <div style={{ overflowX: 'auto' }}>
            <table className="earnings-table">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Subscriber</th>
                  <th>Commission</th>
                  <th>Status</th>
                  <th>Eligible</th>
                </tr>
              </thead>
              <tbody>
                {dashboardData!.earnings.map(entry => (
                  <tr key={entry.id}>
                    <td>{formatDate(entry.created_at)}</td>
                    <td style={{ color: 'var(--text-secondary)' }}>{entry.referred_user_email}</td>
                    <td style={{ fontWeight: 600 }}>{formatCurrency(entry.amount_cents)}</td>
                    <td>
                      <span
                        className="earnings-status-pill"
                        style={STATUS_STYLES[entry.status] ?? STATUS_STYLES.voided}
                      >
                        {entry.status.charAt(0).toUpperCase() + entry.status.slice(1)}
                      </span>
                    </td>
                    <td style={{ padding: '9px 12px', borderBottom: '1px solid var(--stroke)', color: 'var(--text-secondary)', fontSize: '0.8rem', whiteSpace: 'nowrap' }}>
                      {entry.eligible_from
                        ? new Date(entry.eligible_from).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' })
                        : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </GlassCard>
      ) : (
        hasAnyReferrals && (
          <GlassCard style={{ marginTop: '1.25rem', textAlign: 'center', padding: '1.5rem', color: 'var(--text-secondary)', fontSize: '0.875rem' }}>
            No earnings recorded yet. Share your referral link to get started.
          </GlassCard>
        )
      )}

      {/* ── Referred Users ───────────────────────────────────────────────── */}
      {(referredUsers?.total ?? 0) > 0 && (
        <GlassCard style={{ marginTop: '1.25rem', marginBottom: '2rem' }} padding="none">
          <div style={{ padding: '1.25rem 1.25rem 0.75rem', borderBottom: '1px solid var(--stroke)' }}>
            <h2 style={{ margin: 0, fontSize: '1.1rem', fontWeight: 600, color: 'var(--text)' }}>
              Referred Users ({referredUsers!.total})
            </h2>
          </div>
          <ul style={{ listStyle: 'none', margin: 0, padding: '0.5rem 0' }}>
            {referredUsers!.referred_users.map(u => (
              <li
                key={u.id}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.75rem',
                  padding: '0.65rem 1.25rem',
                  borderBottom: '1px solid var(--divider)',
                  fontSize: '0.875rem',
                }}
              >
                <span style={{ flex: 1, color: 'var(--text)' }}>{u.email_masked}</span>
                <span style={{ color: 'var(--text-secondary)', whiteSpace: 'nowrap' }}>
                  {formatDate(u.joined_at)}
                </span>
                {u.plan && (
                  <span style={{ color: 'var(--text-secondary)', textTransform: 'capitalize', whiteSpace: 'nowrap' }}>
                    {u.plan}
                  </span>
                )}
                {u.is_active_subscriber ? (
                  <span
                    className="earnings-status-pill"
                    style={STATUS_STYLES.paid}
                  >
                    Active
                  </span>
                ) : (
                  <span
                    className="earnings-status-pill"
                    style={STATUS_STYLES.voided}
                  >
                    Inactive
                  </span>
                )}
              </li>
            ))}
          </ul>
        </GlassCard>
      )}
    </div>
  );
};

export default AffiliateDashboard;
